import { UserRole } from '@/shared/types';

/**
 * Mapeo de roles a nombres amigables y cortos para mostrar en la UI
 * 
 * Estos nombres son más cortos y amigables que los labels completos,
 * ideales para mostrar en headers, badges y lugares con espacio limitado.
 */
export const ROLE_DISPLAY_NAMES: Record<UserRole | string, string> = {
  // Roles Globales
  [UserRole.SUPER_ADMIN]: 'Super Admin',
  
  // Roles de Negocio (los *_ADMIN son administradores de su organización, no la empresa)
  [UserRole.AGENCY_ADMIN]: 'Admin Agencia',
  [UserRole.BROKER_MANAGER_ADMIN]: 'Admin Broker',
  [UserRole.PROJECT_DEVELOPER_ADMIN]: 'Admin Promotora',
  [UserRole.AGENCY_AGENT_USER]: 'Agente',

  // Roles de Soporte
  [UserRole.ACCOUNTING_TEAM]: 'Contabilidad',
  [UserRole.OPERATIONAL_TEAM]: 'Operaciones',
  [UserRole.CONDOMINIUM_ADMIN]: 'Admin Condominio',
  
  // Roles Especializados
  [UserRole.LEGAL_MODULE]: 'Legal',
  
  // Roles de Cliente
  [UserRole.BUYER_CLIENT]: 'Cliente',
  
  // Rol de Onboarding
  [UserRole.PENDING_ONBOARDING]: 'Onboarding',
  
  // Deprecated (mantener para compatibilidad)
  REAL_ESTATE_AGENT_ADMIN: 'Agente',
  BROKER_MANAGER_USER: 'Broker',
};

/**
 * Función centralizada para obtener el nombre amigable de un rol
 * 
 * @param role - El rol del usuario (puede ser string o UserRole enum)
 * @returns El nombre amigable del rol, o el rol original si no se encuentra en el mapeo
 * 
 * @example
 * ```tsx
 * getRoleDisplayName(UserRole.BROKER_MANAGER_ADMIN) // "Broker"
 * getRoleDisplayName('AGENCY_ADMIN') // "Admin Agencia"
 * getRoleDisplayName('UNKNOWN_ROLE') // "UNKNOWN_ROLE"
 * ```
 */
export const getRoleDisplayName = (role: UserRole | string | null | undefined): string => {
  if (!role) {
    return 'Usuario';
  }
  
  // Normalizar el rol a string
  const roleString = typeof role === 'string' ? role : role;
  
  // Buscar en el mapeo
  const displayName = ROLE_DISPLAY_NAMES[roleString];
  
  // Si no se encuentra, intentar una transformación básica como fallback
  if (!displayName) {
    // Convertir SNAKE_CASE a Title Case como último recurso
    return roleString
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  
  return displayName;
};

